import React from 'react';
import produce from 'immer';
import { Link } from 'react-router-dom';

import { IProps, IState } from './types';
import { ActionForAuthentication } from '../../types';

import toaster, { Theme } from '../../../../shared/toaster';
import Button from '../../../../shared/design-system/components/atoms/button';
import Input from '../../../../shared/design-system/components/input';

import AuthHeader from '../auth-header';
import AuthContent from '../auth-content';
import AuthFooter from '../auth-footer';
import ButtonContainer from '../button-container';
import PrivacyPolicy from '../privacy-policy';
import OauthButtons from '../oauth-buttons';

import {
  // getCookie,
  getCurrentTimeZone,
  initializeThirdPartyIntegrations,
  redirectWithoutRefresh,
  getCurrentToken,
  getGleapAnonymousUserId,
  getGleapUserHash,
  setGleapAnonymousUserId,
  setGleapAnonymousUserHash,
  setGleapUser,
  setGleapUserHash,
} from '../../../../shared/utils';
import {
  executeOnRequestStatus,
  executeOnRequestStatusWithPrevStatusCheck,
} from '../../../../shared/utils/execute-on-request-status';
import { validate } from './validator';
import { getIsRequestPending } from '../../../../shared/utils/get-request-status';

class SignupForm extends React.PureComponent<IProps, IState> {
  private readonly v3 = 'V3';

  private turnstileContainerRef = React.createRef<HTMLDivElement>();

  private turnstileWidgetId: string | undefined;

  constructor(props: IProps) {
    super(props);

    this.state = {
      values: {
        email: props.email ? props.email : '',
      },
      errors: {
        email: '',
      },
      dirty: {
        email: false,
      },
    };

    this.onInputChange = this.onInputChange.bind(this);
    this.onInputBlur = this.onInputBlur.bind(this);
    this.onFormSubmit = this.onFormSubmit.bind(this);
    this.hideErrorHandler = this.hideErrorHandler.bind(this);
  }

  componentDidMount() {
    this.renderTurnstile();

    const { sendGetAnonymousUserHashRequest } = this.props;

    const isTokenPresent = !!getCurrentToken();
    const existingGleapUserHash = getGleapUserHash();
    const existingAnonymousUserId = getGleapAnonymousUserId();
    if (
      !isTokenPresent &&
      !(existingGleapUserHash && existingAnonymousUserId)
    ) {
      sendGetAnonymousUserHashRequest();
    }
  }

  componentDidUpdate(prevProps: Readonly<IProps>) {
    const {
      signupRequestStatus,
      signupRequestError,
      showError,
      acceptInvitationRequestStatus,
      trackingId,
      firstName,
      lastName,
      email,
      anonymousUserHashStatus,
      anonymousUserHash,
      anonymousUserId,
    } = this.props;

    executeOnRequestStatusWithPrevStatusCheck({
      status: anonymousUserHashStatus,
      prevStatus: prevProps.anonymousUserHashStatus,
      onSuccess: () => {
        if (anonymousUserHash && anonymousUserId) {
          setGleapUserHash(anonymousUserHash);
          setGleapAnonymousUserHash(anonymousUserHash);
          setGleapAnonymousUserId(anonymousUserId);
          setGleapUser({ shAccountId: '' });
        }
      },
    });

    if (
      acceptInvitationRequestStatus !== prevProps.acceptInvitationRequestStatus
    ) {
      executeOnRequestStatus({
        status: acceptInvitationRequestStatus,
        onSuccess: () => {
          initializeThirdPartyIntegrations({
            trackingId,
            firstName,
            lastName,
            email,
          });
        },
      });
    }

    // redirectWithoutRefresh({
    //   pathname: '/sequence',
    //   search: 'signup=completed',
    // });
    executeOnRequestStatusWithPrevStatusCheck({
      status: signupRequestStatus,
      prevStatus: prevProps.signupRequestStatus,
      onSuccess: () => {
        const { values } = this.state;

        const queryParams = new URLSearchParams('');
        queryParams.set('email', values.email);

        redirectWithoutRefresh(`/welcome?${queryParams.toString()}`);
      },
      onFailed: () => {
        if (this.turnstileWidgetId !== undefined) {
          (window as any).turnstile?.reset(this.turnstileWidgetId);
        }
        if (showError) {
          toaster.error(signupRequestError.message, { theme: Theme.New });
          this.hideErrorHandler();
        }
      },
    });
  }

  componentWillUnmount() {
    const { hideError } = this.props;
    hideError?.();
    if (this.turnstileWidgetId !== undefined) {
      (window as any).turnstile?.remove(this.turnstileWidgetId);
    }
  }

  onInputChange(value, e) {
    const { name } = e.target;
    this.setState(
      produce((draft) => {
        draft.values[name] = value;
        draft.dirty[name] = true;
      }),
    );
  }

  onInputBlur(e) {
    const { name } = e.target;
    this.setState(
      produce((draft) => {
        if (draft.dirty[name]) {
          draft.errors[name] = validate(name, draft.values[name]);
        }
      }),
    );
  }

  onFormSubmit(e) {
    e.preventDefault();

    const { dirty, errors, values: errorValues } = this.state;

    const dirtyRef = { ...dirty };
    const dirtyKeys = Object.keys(dirtyRef);

    dirtyKeys.forEach((key) => {
      dirtyRef[key] = true;
    });

    const errorsRef = { ...errors };
    const errorsKeys = Object.keys(errorsRef);
    let isError = false;

    errorsKeys.forEach((key) => {
      const error = validate(key, errorValues[key]);

      errorsRef[key] = error;
      isError = isError || !!error;
    });

    this.setState({ errors: errorsRef, dirty: dirtyRef });

    if (isError) {
      return;
    }

    let timeZone = getCurrentTimeZone();
    if (timeZone === 'Asia/Calcutta') {
      timeZone = 'Asia/Kolkata';
    }
    const { values } = this.state;
    const { email } = values;
    // const landingPage = getCookie('sh_ulp') || '';
    const landingPage = window.location.href;
    const { sendSignupRequest } = this.props;

    const rewardfulReferralId = window.tolt_referral ?? '';
    const captchaToken =
      this.turnstileWidgetId !== undefined
        ? (window as any).turnstile?.getResponse(this.turnstileWidgetId) || ''
        : '';
    sendSignupRequest(
      email,
      landingPage,
      timeZone,
      rewardfulReferralId,
      captchaToken,
    );
  }

  hideErrorHandler() {
    const { hideError } = this.props;
    hideError?.();
  }

  renderTurnstile() {
    const { REACT_APP_TURNSTILE_SITE_KEY: siteKey } = process.env;
    if (!siteKey || !this.turnstileContainerRef.current) return;

    const extendedWindow = window as Window & { turnstile?: any };
    const { turnstile } = extendedWindow;
    if (turnstile) {
      this.turnstileWidgetId = turnstile.render(
        this.turnstileContainerRef.current,
        {
          sitekey: siteKey,
        },
      );
    } else {
      const script = document.querySelector(
        'script[src*="challenges.cloudflare.com/turnstile"]',
      ) as HTMLScriptElement | null;
      script?.addEventListener('load', () => this.renderTurnstile(), {
        once: true,
      });
    }
  }

  render() {
    const { values, errors } = this.state;
    const { signupRequestStatus, email } = this.props;
    const isLoading = getIsRequestPending(signupRequestStatus);
    const isSignUpButtonDisable =
      !!validate('email', values.email) || isLoading;

    return (
      <>
        <AuthHeader
          title="Sign up for free"
          description="No obligations, no credit card required"
        />

        <AuthContent>
          <OauthButtons type={ActionForAuthentication.Signup} email={email} />

          <form className="auth-form" onSubmit={this.onFormSubmit}>
            <Input
              name="email"
              label="Email"
              placeholder="johndoe@example.com"
              value={values.email}
              variant={errors.email && Input.Variant.Error}
              caption={errors.email}
              onChange={this.onInputChange}
              onBlur={this.onInputBlur}
              disabled={isLoading}
            />

            <div ref={this.turnstileContainerRef} className="cf-turnstile" />

            <ButtonContainer>
              <Button
                variant={Button.Variant.Primary}
                type={Button.Type.Submit}
                isLoading={isLoading}
                disabled={isSignUpButtonDisable}
                className="auth-action-btn"
              >
                Sign Up
              </Button>
            </ButtonContainer>
          </form>
        </AuthContent>

        <PrivacyPolicy />

        <AuthFooter>
          <p>
            Already have an account? <Link to="/login">Login</Link>
          </p>
        </AuthFooter>
      </>
    );
  }
}

export default SignupForm;
